/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, PrimitiveAtom, WritableAtom, atom, getDefaultStore } from 'jotai';
import { lapDataSelectorFamily } from 'jotai/lapData';
import { qualifyingLapDataReplayCurrentLapSelector } from 'jotai/raceReplay';
import { activeSanctioningBodyFeedAtom, activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import {
  activeQualifyingMetaDataBySeries,
  activeSessionReplayEnabledSelector,
  activeSessionSelector,
} from 'jotai/session';
import { atomFamily } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { LapData, OriginType, QualifyingLapData, RunType, SanctioningBodyEnum } from 'models/viewModels';
import { qualifyingLapDataServiceCollection } from 'services/stream/RSocket';
import {
  PracticeQualifyingVehicleArraySelectorProps,
  QualifyingRunSelectorFamilyProps,
} from './QualifyingLapDataObject';

let listenerId: string;

export const enum RunNames {
  Round1 = 'ROUND 1',
  Round2 = 'ROUND 2',
  Round3 = 'ROUND 3',
  Round4 = 'ROUND 4',
  GroupA = 'GROUP A',
  GroupB = 'GROUP B',
  FinalRound = 'FINALROUND',
  Final = 'FINAL',
  All = 'ALL',
}

export const RunNamesMap: Record<RunNames, string> = {
  [RunNames.Round1]: 'ROUND 1',
  [RunNames.Round2]: 'ROUND 2',
  [RunNames.Round3]: 'ROUND 3',
  [RunNames.Round4]: 'ROUND 4',
  [RunNames.GroupA]: 'GROUP A',
  [RunNames.GroupB]: 'GROUP B',
  [RunNames.FinalRound]: 'FINALROUND',
  [RunNames.Final]: 'FINAL',
  [RunNames.All]: 'ALL',
};

export type RunNamesType = `${RunNames}`;

export const qualifyingLapDataSelectorFamily = atomFamily<OriginType, Atom<QualifyingLapData[]>>(
  (dataSource: OriginType) =>
    atom<QualifyingLapData[]>((get) => {
      const activeSanctioningBodyFeed = get(activeSanctioningBodyFeedAtom);
      const isReplay = get(activeSessionReplayEnabledSelector);
      return !isReplay
        ? get(qualifyingLapDataAtomFamily(!activeSanctioningBodyFeed ? dataSource : activeSanctioningBodyFeed))
        : get(qualifyingLapDataReplayCurrentLapSelector);
    })
);

const currentQualifyingLapDataAtomFamily = atomFamily<OriginType, PrimitiveAtom<QualifyingLapData[]>>(
  (dataSource: OriginType) => atom<QualifyingLapData[]>([])
);

const initQualifyingLapDataAtomFamily = atomFamily<OriginType, WritableAtom<null, [], () => void>>(
  (dataSource: OriginType) => {
    const newAtom = atom<null, [], () => void>(null, (get, set) => {
      const sanctioningBody = get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.NASCAR;
      listenerId = qualifyingLapDataServiceCollection
        .get(sanctioningBody)!
        .get(dataSource)!
        .addListener(
          async (qualifyingLapData) => {
            set(currentQualifyingLapDataAtomFamily(dataSource), qualifyingLapData);
          },
          async () => {
            set(currentQualifyingLapDataAtomFamily(dataSource), []);
          }
        );
      return () => {
        qualifyingLapDataServiceCollection.get(sanctioningBody)?.get(dataSource)?.removeListener(listenerId);
      };
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  }
);

const qualifyingLapDataAtomFamily = atomFamily<OriginType, Atom<QualifyingLapData[]>>((dataSource: OriginType) =>
  atom<QualifyingLapData[]>((get) => {
    get(initQualifyingLapDataAtomFamily(dataSource));
    return get(currentQualifyingLapDataAtomFamily(dataSource));
  })
);

const TOP_VEHICLES_COUNT_WITH_GROUPS = 5;
const TOP_VEHICLES_COUNT_WITHOUT_GROUPS = 10;
const DEFAULT_RUNNING_POSITION = 999;

const filterLapDataByRunNames = (
  lapData: QualifyingLapData[],
  runNames: string[],
  shouldInclude: boolean
): QualifyingLapData[] => {
  return lapData.filter((item) => {
    const itemRunNameUpperCase = item.runName.toUpperCase();
    const runNameMatch = runNames.some((rn) => itemRunNameUpperCase.includes(rn.toUpperCase()));
    return shouldInclude ? runNameMatch : !runNameMatch;
  });
};

//TODO: remove later if we no longer need to sort and assign positions in the UI.
/*const sortLapDataByBestTime = (lapData: QualifyingLapData[]): QualifyingLapData[] => {
  return lapData
    .sort((a, b) =>
      a.lapsCompleted > 0 && b.lapsCompleted > 0 ? a.bestLapTime - b.bestLapTime : b.bestLapTime - a.bestLapTime
    )
    .map((item, index) => ({
      ...item,
      runningPosition: item.lapsCompleted > 0 ? index + 1 : DEFAULT_RUNNING_POSITION,
    }));
};*/

//TODO: remove later if we no longer need to sort and assign positions in the UI.
/*const filterAndSortLapDataByRunNames = (lapData: QualifyingLapData[], runNames: string[]): QualifyingLapData[] => {
  return sortLapDataByBestTime(filterLapDataByRunNames(lapData, runNames, true));
};*/

const topVehiclesCountSelector = atom<number>((get) => {
  const qualifyingMetadata = get(activeQualifyingMetaDataBySeries);

  return qualifyingMetadata && qualifyingMetadata?.groupsList?.length > 0
    ? TOP_VEHICLES_COUNT_WITH_GROUPS
    : TOP_VEHICLES_COUNT_WITHOUT_GROUPS;
});

const sortAndMapFinalRoundData = (lapData: QualifyingLapData[], runNames: string[]): QualifyingLapData[] => {
  const nonFinalRunNames = [
    ...new Set(
      lapData
        .filter((item) => !runNames.some((finalRunNames) => item.runName.includes(finalRunNames)))
        .map((item) => item.runName)
    ),
  ];

  const finalRunNames = [
    ...new Set(
      lapData
        .filter((item) => runNames.some((finalRunNames) => item.runName.includes(finalRunNames)))
        .map((item) => item.runName)
    ),
  ];

  const nonFinalTopVehicles = nonFinalRunNames.reduce((acc, runName) => {
    const runData = filterLapDataByRunNames(lapData, [runName], true);
    return acc.concat(runData);
  }, [] as QualifyingLapData[]);

  const nonFinalRemainingVehicles = finalRunNames.reduce((acc, runName) => {
    const runData = filterLapDataByRunNames(lapData, [runName], true);
    return acc.concat(runData);
  }, [] as QualifyingLapData[]);

  const topVehicles = getDefaultStore().get(topVehiclesCountSelector);
  const finalRoundLapData = nonFinalTopVehicles
    .filter((item) => item.runningPosition <= topVehicles && item.lapsCompleted > 0)
    .sort((a, b) =>
      // sort reverse to assign QPos to order in which they will run final
      a.bestLapTime === b.bestLapTime ? a.runningPosition - b.runningPosition : b.bestLapTime - a.bestLapTime
    )
    .map((item, index) => ({
      ...item,
      qualifyingPosition: index + 1,
      // Set isDatabaseRecord to true to trigger invalidPositionRenderer for runningPosition
      isDatabaseRecord: true,
      lapsCompleted: 0,
      // Default to position 10.1 since it's the last position in the final round
      // There was an issue where two drivers were listed at position 10 because one driver hadn't run yet.
      // Now, the driver who hasn't run will be positioned at 10.1.
      runningPosition: 10.1,
    }));

  const remainingVehicleData = nonFinalRemainingVehicles
    .filter(
      (item) =>
        item.runningPosition > topVehicles &&
        item.lapsCompleted > 0 &&
        !finalRoundLapData.some((finalItem) => finalItem.vehicleNumber === item.vehicleNumber)
    )
    .sort((a, b) =>
      a.bestLapTime === b.bestLapTime ? a.runningPosition - b.runningPosition : a.bestLapTime - b.bestLapTime
    );

  const remainingStartingOrderData = remainingVehicleData
    // Want to trigger invalidPositionRenderer for qualifyingPosition for all appended data
    .map((item, index) => ({
      ...item,
      runningPosition: item.runningPosition,
      qualifyingPosition: DEFAULT_RUNNING_POSITION,
    }));
  return [...finalRoundLapData, ...remainingStartingOrderData];
};

const findMissingPositions = (qualifyingPositions: QualifyingLapData[], topCount: number): number[] => {
  const occupiedPositions = qualifyingPositions
    .filter((res) => res.runningPosition !== DEFAULT_RUNNING_POSITION && res.runningPosition <= topCount)
    .map((res) => res.runningPosition);

  const allPositions = Array.from({ length: topCount }, (_, i) => i + 1);
  return allPositions.filter((position) => !occupiedPositions.includes(position));
};

export const qualifyingRunNameSelectorFamily = atomFamily<QualifyingRunSelectorFamilyProps, Atom<QualifyingLapData[]>>(
  ({ dataSource, runName, isFinalRound, returnAllData }: QualifyingRunSelectorFamilyProps) =>
    atom<QualifyingLapData[]>((get) => {
      const qualifyingLapData = get(qualifyingLapDataSelectorFamily(dataSource));
      if (!isFinalRound) {
        const returnData = returnAllData
          ? qualifyingLapData
          : filterLapDataByRunNames(qualifyingLapData, runName, true);
        return returnData;
      }

      // Find top vehicles and sort them based on fastest lap
      const qualifyingPositions = sortAndMapFinalRoundData(qualifyingLapData, runName);

      // Get final round from the stream
      const streamResponse = filterLapDataByRunNames(qualifyingLapData, runName, true);
      // Merge results of the final round and the qualifying rounds while substituting the final rounds results
      const updatedQualifyingPositions = qualifyingPositions.reduce(
        (prev: QualifyingLapData[], curr: QualifyingLapData) => {
          const vehicleIndex = streamResponse.findIndex(
            (item) =>
              item.vehicleNumber === curr.vehicleNumber &&
              Math.floor(item.runningPosition / (TOP_VEHICLES_COUNT_WITHOUT_GROUPS + 1)) ===
                Math.floor(curr.runningPosition / (TOP_VEHICLES_COUNT_WITHOUT_GROUPS + 1))
          );

          if (vehicleIndex !== -1) {
            prev.push({
              ...streamResponse[vehicleIndex],
              qualifyingPosition: curr.qualifyingPosition,
              runningPosition:
                streamResponse[vehicleIndex].runningPosition &&
                streamResponse[vehicleIndex].runningPosition !== DEFAULT_RUNNING_POSITION
                  ? streamResponse[vehicleIndex].runningPosition
                  : curr.qualifyingPosition,
            });
            streamResponse.splice(vehicleIndex, 1);
          } else {
            prev.push(curr);
          }
          return prev;
        },
        []
      );
      const finalResult = updatedQualifyingPositions;

      // Account for a scenario where a vehicle qualifies and then either doesnt compete in final or no data comes in for that vehicle
      if (streamResponse.length < TOP_VEHICLES_COUNT_WITHOUT_GROUPS) {
        // Grab the final round unmerged rows
        const missedRows = finalResult
          .filter((res) => res.runningPosition === DEFAULT_RUNNING_POSITION)
          .sort((a, b) => b.bestLapTime - a.bestLapTime);

        // Confirm the amount of unsubstituted rows matches the missing final round vehicles
        if (missedRows.length === TOP_VEHICLES_COUNT_WITHOUT_GROUPS - streamResponse.length) {
          // Find what positions are missing from top and fill them in
          const missingPositions = findMissingPositions(finalResult, TOP_VEHICLES_COUNT_WITHOUT_GROUPS);
          missedRows.forEach((item, index) => {
            item.runningPosition = missingPositions[index];
          });
          // Grab final 10 of filled results
          const filledFinalResult = finalResult.filter(
            (res) => res.runningPosition <= TOP_VEHICLES_COUNT_WITHOUT_GROUPS
          );
          // backfill with new result
          return filledFinalResult.concat(
            finalResult.filter(
              (res) =>
                res.runningPosition > TOP_VEHICLES_COUNT_WITHOUT_GROUPS &&
                res.runningPosition !== DEFAULT_RUNNING_POSITION
            )
          );
        }
      }
      return finalResult;
    }),
  //Prevents infinite loop
  (a, b) => isEqual(a, b)
);

export const practiceQualifyingHiddenVehiclesSelectorFamily = atomFamily<
  PracticeQualifyingVehicleArraySelectorProps,
  Atom<Record<string, boolean>>
>(
  (props: PracticeQualifyingVehicleArraySelectorProps) =>
    atom<Record<string, boolean>>((get) => {
      if (props.runName === 'ALL' || props.runType === RunType.RACE) {
        return {};
      }
      const lapData =
        props.runType === RunType.QUALIFYING
          ? get<QualifyingLapData[]>(qualifyingLapDataSelectorFamily(props.dataSource))
          : get<LapData[]>(lapDataSelectorFamily(props.dataSource));
      if (props.runName.includes(RunNames.All.valueOf())) {
        let hiddenMap: Record<string, boolean> = {};
        lapData.map((lap) => (hiddenMap[lap.vehicleNumber] = false));
        return hiddenMap;
      }
      // Tech-Debt: Lapdata for qualifying & practice are a similar data objects but neither is an extension of the same interface currently
      const lapDataFixed: Array<(typeof lapData)[number]> = lapData;

      return lapDataFixed.reduce((acc, lap) => {
        // Tech-debt: Current runName property is a large string that has to be searched via contains, without this check it breaks for FINAL
        if (acc[lap.vehicleNumber] === undefined || acc[lap.vehicleNumber] === true)
          acc[lap.vehicleNumber] = !lap.runName.toUpperCase().includes(props.runName.toUpperCase());
        return acc;
      }, {} as Record<string, boolean>);
    }),
  (a, b) => isEqual(a, b)
);

export const qualifyingLapDataMobileSelector = atom<QualifyingLapData[]>((get) => {
  const formattedRowData = get(qualifyingLapDataAtomFamily(OriginType.MULTILOOP)).map((data) => {
    if (data.runName.toUpperCase().includes('GROUP A')) {
      return {
        ...data,
        runName: 'A',
      };
    } else if (data.runName.toUpperCase().includes('GROUP B')) {
      return {
        ...data,
        runName: 'B',
      };
    } else if (data.runName.toUpperCase().includes('FINAL')) {
      return {
        ...data,
        runName: 'Final',
      };
    } else {
      return data;
    }
  });
  return formattedRowData;
});

export const mobilePracticeQualifyingGroupsSelector = atom<string[]>((get) => {
  const data =
    get(activeSessionSelector)?.sessionType === RunType.QUALIFYING
      ? get(qualifyingLapDataAtomFamily(OriginType.MULTILOOP))
      : get(lapDataSelectorFamily(OriginType.MULTILOOP));
  const groups = data.map((data) => {
    return data.runName;
  });
  const uniqueGroups = groups.filter((value, index, array) => {
    return array.indexOf(value) === index;
  });
  uniqueGroups.sort().unshift('All');
  return uniqueGroups;
});
